import { EntityBryntumTab } from "./EntityBryntumTab";
import { Tabs, TabsContent } from "@/modern/components/ui/tabs";
import { cn } from "@/modern/utils";
import { EntityTitleBar } from "./components/EntityTitleBar";
import { EntityPillBar } from "./components/EntityPillBar";
import { Timeline } from "../entityViews/timeline/Timeline";
import { EntityManagerProvider, useEntityManagerContext } from "./context/EntityManagerContext";
import { Drawer, DrawerContent, DrawerTitle } from "@/modern/components/ui/drawer";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useEffect, useLayoutEffect, useState, useRef } from "react";

const EntityManager = () => {
  const { record, entityConfig, activeTab, setActiveTab } = useEntityManagerContext();
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);

  return (
    <div className="entity-page flex bg-background h-full flex-1 w-full min-w-0 [&_.swivu-modern-grid-tbar]:bg-transparent absolute inset-0" style={{ fontSize: "16px" }}>
      <Tabs
        defaultValue="0"
        className={cn(
          "flex-1 min-w-0 grid [grid-template-areas:'header_header'_'timeline_panel'] [grid-template-rows:auto_1fr] transition-all duration-300",
          isTimelineOpen
            ? "[grid-template-columns:minmax(250px,20%)_auto]"
            : "[grid-template-columns:48px_auto]"
        )}
        onValueChange={setActiveTab}
      >
        <div className="[grid-area:header] border-b min-w-0">
          <EntityTitleBar />
          <EntityPillBar record={record} entityConfig={entityConfig} showColor={record.isTask} />
        </div>
        <main className="flex flex-col [grid-area:panel] overflow-y-auto">
          {entityConfig.tabs.map(({ type, Tab, ...tabConfig }, index) => {
            const isActiveTab = activeTab === String(index);
            return (
              <TabsContent key={index} value={index.toString()} className={cn("h-full !outline-none !shadow-none !ring-0")} forceMount={true} hidden={!isActiveTab}>
                {type === "bryntum" ? (
                  <EntityBryntumTab widget={Tab} hidden={!isActiveTab} {...tabConfig} />
                ) : (
                  <Tab />
                )}
              </TabsContent>
            );
          })}
        </main>
        <div className="[grid-area:timeline] flex min-h-0 flex-col flex-1">
          <Timeline onOpenChange={setIsTimelineOpen} />
        </div>
      </Tabs>
    </div>
  );
};

const snapPoints = [0.23, 1];
const defaultSnapPoint = snapPoints[1];

export const EntityManagerView = () => {
  const { entityManager, editorHandler } = useSwivuBryntumContext();
  const { isOpen: isEntityManagerOpen, entityId, entityType } = entityManager;
  const [snap, setSnap] = useState<number | string | null>(defaultSnapPoint);
  const hasOpenedEditors = editorHandler.editors.length > 0;
  const isDragging = useRef(false);

  const setSnapPointToDefault = () => {
    if (snap !== defaultSnapPoint) {
      setSnap(defaultSnapPoint);
    }
  }

  /* sets pointer events properties: pointer-events: auto; on the body to allow interactions with modal dialogs
  Found resolution here: https://github.com/emilkowalski/vaul/issues/497#issuecomment-2452503724*/
  useEffect(() => {
    if (isEntityManagerOpen) {
      setTimeout(() => {
        document.body.style.pointerEvents = "auto";
      });
    }
  }, [isEntityManagerOpen]);

  useEffect(() => {
    if (isEntityManagerOpen) {
      setSnapPointToDefault(); // Reset to default snap point when drawer opens or the entity id or type changes
    }
  }, [entityId, entityType, isEntityManagerOpen])

  /* We need to use this hack to prevent the drawer from blocking interactions with modal dialogs, if not applied the input fields in the @TaskGeneralEditor.tsx are not focusable
  found resolution here: https://github.com/emilkowalski/vaul/issues/497#issuecomment-2452503724
  */
  useLayoutEffect(() => {
    document.addEventListener("focusin", (e) => e.stopImmediatePropagation());
    document.addEventListener("focusout", (e) => e.stopImmediatePropagation());
  }, []);

  return (
    <Drawer
      modal={false}
      open={isEntityManagerOpen}
      onOpenChange={(open) => {
        if (!open) {
          entityManager.closeEntityManager();
        }
      }}
      snapPoints={snapPoints}
      activeSnapPoint={snap}
      setActiveSnapPoint={setSnap}
      snapToSequentialPoint={true}
      closeThreshold={0.5} /* Close if user drags down 50% of the height of the drawer */
      dismissible={!hasOpenedEditors} /* Prevent the drawer from closing when any editor is opened */
    >
      {/* The !select-text class needs to be applied to enable user selection in the content inside of the drawer component, the vaul library disables it by default.
      Found resolution here: https://github.com/emilkowalski/vaul/issues/346#issuecomment-2132384928 */}
      <DrawerContent className="h-[calc(100vh-3.5rem)] !select-text outline-none">
        <DrawerTitle className="sr-only">Entity Manager</DrawerTitle>
        <div
          onMouseDown={() => isDragging.current = false}
          onMouseMove={() => isDragging.current = true}
          onClick={() => !isDragging.current && entityManager.closeEntityManager()}
          className="absolute top-3 left-1/2 -translate-x-1/2 z-50 cursor-pointer p-4 -m-4"
          aria-label="Close drawer"
        >
          <div className="h-[0.325rem] w-[6.6rem] bg-accent-contrast hover:bg-accent-contrast/90 transition-colors rounded-full" />
        </div>
        <EntityManagerProvider>
          <EntityManager />
        </EntityManagerProvider>
      </DrawerContent>
    </Drawer>
  );
};
